import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useAuth, useChatInteract } from '@chainlit/react-client';

import FonMark from '@/components/FonMark';
import MaterialIcon from '@/components/MaterialIcon';
import UserAvatar from '@/components/UserAvatar';
import { useSidebar } from '@/components/ui/sidebar';

import { userSettingsOpenState } from '@/state/project';
import { profileState } from '@/state/user';

import SearchChats from './Search';
import { ThreadHistory } from './ThreadHistory';

type SidebarActionsProps = {
  expanded: boolean;
  onNewChat: () => void;
  onSearch: () => void;
};

function useSidebarCopy() {
  const { i18n } = useTranslation();
  let language = i18n.resolvedLanguage;

  try {
    language = localStorage.getItem('fongpt-language') || language;
  } catch {
    // Keep i18n's current language when local storage is unavailable.
  }

  return language?.toLowerCase().startsWith('sr')
    ? {
        chats: 'Razgovori',
        closeSidebar: 'Zatvori bočnu traku',
        home: 'Početna stranica FONGPT-a',
        newChat: 'Novi razgovor',
        openSidebar: 'Otvori bočnu traku',
        search: 'Pretraži',
        searchChats: 'Pretraži razgovore',
        settings: 'Podešavanja',
        signOut: 'Odjavi se',
        userMenu: 'Korisnički meni'
      }
    : {
        chats: 'Chats',
        closeSidebar: 'Close sidebar',
        home: 'FONGPT home',
        newChat: 'New Chat',
        openSidebar: 'Open sidebar',
        search: 'Search',
        searchChats: 'Search conversations',
        settings: 'Settings',
        signOut: 'Sign out',
        userMenu: 'User menu'
      };
}

function SidebarActions({
  expanded,
  onNewChat,
  onSearch
}: SidebarActionsProps) {
  const copy = useSidebarCopy();
  const rowClass = expanded
    ? 'fongpt-sidebar-nav-item flex w-full items-center gap-3 text-left'
    : 'fongpt-sidebar-nav-item flex items-center justify-center';

  return (
    <div
      className={`flex w-full flex-col ${
        expanded ? 'gap-0.5' : 'items-center gap-0.5'
      }`}
    >
      <button
        type="button"
        onClick={onNewChat}
        className={rowClass}
        title={copy.newChat}
        aria-label={copy.newChat}
      >
        <MaterialIcon name="edit_square" />
        {expanded ? <span>{copy.newChat}</span> : null}
      </button>
      <button
        type="button"
        onClick={onSearch}
        className={rowClass}
        title={copy.searchChats}
        aria-label={copy.searchChats}
      >
        <MaterialIcon name="search" />
        {expanded ? <span className="flex-1">{copy.search}</span> : null}
      </button>
    </div>
  );
}

function SidebarUserMenu({
  compact,
  displayName,
  identifier,
  providerImage,
  onSettings,
  onLogout
}: {
  compact?: boolean;
  displayName: string;
  identifier?: string;
  providerImage?: unknown;
  onSettings: () => void;
  onLogout: () => void;
}) {
  const copy = useSidebarCopy();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dismiss = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, [open]);

  return (
    <div
      ref={ref}
      className={`relative ${compact ? 'flex justify-center' : ''}`}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`fongpt-hover-strong fongpt-sidebar-user flex items-center rounded-xl text-left transition-colors ${
          compact ? 'justify-center rounded-full' : 'w-full gap-2.5 px-2.5'
        }`}
        title={displayName}
        aria-label={copy.userMenu}
      >
        <span className="relative h-7 w-7 shrink-0">
          <UserAvatar
            identifier={identifier}
            displayName={displayName}
            providerImage={providerImage}
            className="h-7 w-7"
            fallbackClassName="text-[10px]"
          />
        </span>
        {!compact ? (
          <span className="min-w-0 flex-1 truncate text-[12px] font-medium">
            {displayName}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          className={`liquid-glass absolute bottom-full z-50 mb-2 flex overflow-hidden rounded-xl shadow-2xl ${
            compact ? 'left-full ml-2 w-44' : 'left-0 right-0'
          }`}
        >
          <div className="w-full py-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onSettings();
              }}
              className="fongpt-hover flex min-h-10 w-full items-center gap-3 px-3 text-left text-sm text-secondary"
            >
              <MaterialIcon name="settings" className="text-[18px]" />
              {copy.settings}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
              className="fongpt-hover-danger flex min-h-10 w-full items-center gap-3 px-3 text-left text-sm text-red-300"
            >
              <MaterialIcon name="logout" className="text-[18px]" />
              {copy.signOut}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function LeftSidebar(props: React.ComponentProps<'aside'>) {
  const copy = useSidebarCopy();
  const navigate = useNavigate();
  const { clear } = useChatInteract();
  const { logout, user } = useAuth();
  const { open, setOpen, openMobile, setOpenMobile, isMobile } = useSidebar();
  const profile = useRecoilValue(profileState);
  const [, setSettingsOpen] = useRecoilState(userSettingsOpenState);
  const [searchOpen, setSearchOpen] = useState(false);
  const [renderExpanded, setRenderExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const isExpanded = isMobile ? openMobile : open;
  const displayName =
    profile?.display_name || user?.identifier?.split('@')[0] || 'Student';

  useEffect(() => {
    if (isExpanded) {
      setIsClosing(false);
      setRenderExpanded(true);
      return;
    }
    if (!renderExpanded) return;
    setIsClosing(true);
    const timer = window.setTimeout(() => {
      setRenderExpanded(false);
      setIsClosing(false);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [isExpanded, renderExpanded]);

  const closeMobile = () => {
    if (isMobile) setOpenMobile(false);
  };
  const newChat = () => {
    clear();
    navigate('/');
    closeMobile();
  };
  const openSearch = () => {
    setSearchOpen(true);
    closeMobile();
  };
  const openSettings = () => {
    setSettingsOpen(true);
    closeMobile();
  };

  return (
    <>
      <SearchChats open={searchOpen} onOpenChange={setSearchOpen} />
      {isMobile && isExpanded ? (
        <button
          type="button"
          aria-label={copy.closeSidebar}
          onClick={() => setOpenMobile(false)}
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-[2px] md:hidden"
        />
      ) : null}

      {renderExpanded ? (
        <aside
          {...props}
          className={`fongpt-sidebar fongpt-sidebar-expanded fixed left-0 top-0 z-40 flex h-full flex-col ${
            isClosing ? 'animate-sidebar-out' : 'animate-sidebar-in'
          } ${props.className || ''}`}
        >
          <header className="fongpt-sidebar-header flex items-center justify-between px-2">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="fongpt-sidebar-brand flex items-center gap-2"
              aria-label={copy.home}
            >
              <FonMark alt="" className="h-6 w-6" />
              <span>FONGPT</span>
            </button>
            <button
              type="button"
              className="fongpt-hover flex h-9 w-9 items-center justify-center rounded-lg text-secondary"
              onClick={() => (isMobile ? setOpenMobile(false) : setOpen(false))}
              aria-label={copy.closeSidebar}
            >
              <MaterialIcon name="left_panel_close" className="text-[19px]" />
            </button>
          </header>
          <div className="px-1">
            <SidebarActions
              expanded
              onNewChat={newChat}
              onSearch={openSearch}
            />
          </div>
          <div className="fongpt-thread-history flex min-h-0 flex-1 flex-col overflow-hidden px-1">
            <ThreadHistory />
          </div>
          <footer className="fongpt-sidebar-footer mt-auto">
            <SidebarUserMenu
              displayName={displayName}
              identifier={user?.identifier}
              providerImage={user?.metadata?.image}
              onSettings={openSettings}
              onLogout={() => logout(true)}
            />
          </footer>
        </aside>
      ) : null}

      {!isExpanded && !renderExpanded ? (
        <nav
          className="fongpt-sidebar fongpt-sidebar-rail fixed left-0 top-0 z-40 hidden h-full flex-col items-center md:flex"
          aria-label={copy.openSidebar}
          onClick={(event) => {
            const target = event.target as HTMLElement;
            if (!target.closest('button, a, input, select, textarea')) {
              setOpen(true);
            }
          }}
        >
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="fongpt-hover fongpt-sidebar-rail-brand mb-1 grid h-10 w-10 place-items-center rounded-xl"
            aria-label={copy.openSidebar}
          >
            <FonMark className="h-7 w-7" />
          </button>
          <SidebarActions
            expanded={false}
            onNewChat={newChat}
            onSearch={openSearch}
          />
          <footer className="fongpt-sidebar-footer">
            <SidebarUserMenu
              compact
              displayName={displayName}
              identifier={user?.identifier}
              providerImage={user?.metadata?.image}
              onSettings={openSettings}
              onLogout={() => logout(true)}
            />
          </footer>
        </nav>
      ) : null}
    </>
  );
}
